/*
 * O mapa Font Awesome → Lucide do tema.
 *
 * Em vez dos ícones sólidos do Font Awesome, a interface usa o equivalente
 * de traço do Lucide. O META_MAP abaixo é a tabela que liga cada nome do Font
 * Awesome ao desenho do Lucide: é ela que decide quais ícones viram Lucide e
 * quais continuam no Font Awesome.
 *
 * As chaves são os nomes do Font Awesome 6 (`magnifying-glass`, não
 * `search`; `far-bell` para o regular). O Flarum e as extensões ainda usam
 * muitos apelidos do FA 4/5, então o nome passa antes pelo FA_ALIASES.
 *
 * Este arquivo só tem nomes (strings): os desenhos ficam em glyphs.ts.
 */

/**
 * Apelidos antigos do Font Awesome → nome canônico do FA 6 (o das chaves do
 * mapa). Só os que aparecem no core, nas extensões instaladas ou no tema, mais
 * os mais comuns que um admin digita para tags e grupos.
 */
export const FA_ALIASES = {
  'angle-double-down': 'angles-down',
  'angle-double-left': 'angles-left',
  'angle-double-right': 'angles-right',
  'angle-double-up': 'angles-up',
  'arrows-h': 'left-right',
  'arrows-v': 'up-down',
  'bar-chart': 'chart-bar',
  'calendar-alt': 'calendar-days',
  'check-circle': 'circle-check',
  'check-square': 'square-check',
  close: 'xmark',
  'cloud-download-alt': 'cloud-arrow-down',
  'cloud-upload-alt': 'cloud-arrow-up',
  cog: 'gear',
  'comment-alt': 'message',
  edit: 'pen-to-square',
  'ellipsis-h': 'ellipsis',
  'ellipsis-v': 'ellipsis-vertical',
  'envelope-open-text': 'envelope-open',
  'exclamation-circle': 'circle-exclamation',
  'exclamation-triangle': 'triangle-exclamation',
  'external-link': 'up-right-from-square',
  'external-link-alt': 'up-right-from-square',
  'file-alt': 'file-lines',
  'file-archive': 'file-zipper',
  'file-download': 'file-arrow-down',
  'file-upload': 'file-arrow-up',
  'globe-americas': 'earth-americas',
  history: 'clock-rotate-left',
  home: 'house',
  'info-circle': 'circle-info',
  'map-marker-alt': 'location-dot',
  'minus-circle': 'circle-minus',
  'mobile-alt': 'mobile-screen-button',
  'paint-brush': 'paintbrush',
  'pencil-alt': 'pencil',
  'photo-video': 'photo-film',
  'plus-circle': 'circle-plus',
  'plus-square': 'square-plus',
  poll: 'square-poll-vertical',
  'poll-h': 'square-poll-horizontal',
  'question-circle': 'circle-question',
  redo: 'arrow-rotate-right',
  refresh: 'arrows-rotate',
  remove: 'xmark',
  save: 'floppy-disk',
  search: 'magnifying-glass',
  'search-minus': 'magnifying-glass-minus',
  'search-plus': 'magnifying-glass-plus',
  'share-alt': 'share-nodes',
  'shield-alt': 'shield-halved',
  'sign-in-alt': 'right-to-bracket',
  'sign-out-alt': 'right-from-bracket',
  'sliders-h': 'sliders',
  smile: 'face-smile',
  'step-backward': 'backward-step',
  'step-forward': 'forward-step',
  stream: 'bars-staggered',
  sync: 'arrows-rotate',
  'sync-alt': 'rotate',
  'th-large': 'table-cells-large',
  'thumb-tack': 'thumbtack',
  times: 'xmark',
  'times-circle': 'circle-xmark',
  tired: 'face-tired',
  'trash-alt': 'trash-can',
  undo: 'arrow-rotate-left',
  'undo-alt': 'rotate-left',
  'unlock-alt': 'unlock-keyhole',
  'user-cog': 'user-gear',
  'user-edit': 'user-pen',
  'user-friends': 'user-group',
  'user-times': 'user-xmark',
  'vote-yea': 'check-to-slot',
  'volume-mute': 'volume-xmark',
  'volume-up': 'volume-high'
};

/**
 * A tabela principal (nome do Font Awesome → nome do Lucide). As chaves `far-*` são as variantes regulares.
 */
export const META_MAP = {
  'align-left': 'align-left',
  anchor: 'anchor',
  asterisk: 'asterisk',
  award: 'award',
  ban: 'ban',
  bed: 'bed',
  bell: 'bell',
  bold: 'bold',
  book: 'book',
  bookmark: 'bookmark',
  briefcase: 'briefcase',
  calendar: 'calendar',
  'chart-bar': 'chart-bar',
  'chart-column': 'chart-column',
  'chart-line': 'chart-line',
  'chart-pie': 'chart-pie',
  check: 'check',
  circle: 'circle',
  'circle-check': 'circle-check',
  'circle-chevron-down': 'circle-chevron-down',
  'circle-minus': 'circle-minus',
  'circle-plus': 'circle-plus',
  'circle-stop': 'circle-stop',
  'circle-user': 'circle-user',
  clipboard: 'clipboard',
  clock: 'clock',
  code: 'code',
  copy: 'copy',
  crown: 'crown',
  download: 'download',
  eye: 'eye',
  file: 'file',
  'file-image': 'file-image',
  'file-video': 'file-video',
  flag: 'flag',
  folder: 'folder',
  'folder-open': 'folder-open',
  fire: 'flame',
  gift: 'gift',
  globe: 'globe',
  'graduation-cap': 'graduation-cap',
  hand: 'hand',
  headset: 'headset',
  heart: 'heart',
  house: 'house',
  'id-card': 'id-card',
  image: 'image',
  images: 'images',
  inbox: 'inbox',
  italic: 'italic',
  key: 'key',
  keyboard: 'keyboard',
  lightbulb: 'lightbulb',
  link: 'link',
  list: 'list',
  'list-check': 'list-check',
  lock: 'lock',
  medal: 'medal',
  minus: 'minus',
  moon: 'moon',
  paintbrush: 'paintbrush',
  palette: 'palette',
  pause: 'pause',
  pen: 'pen',
  pencil: 'pencil',
  play: 'play',
  plug: 'plug',
  plus: 'plus',
  'power-off': 'power-off',
  reply: 'reply',
  rocket: 'rocket',
  rss: 'rss',
  scroll: 'scroll',
  share: 'share',
  shuffle: 'shuffle',
  signal: 'signal',
  'spell-check': 'spell-check',
  'square-check': 'square-check',
  'square-plus': 'square-plus',
  stamp: 'stamp',
  star: 'star',
  sun: 'sun',
  table: 'table',
  tag: 'tag',
  tags: 'tags',
  trophy: 'trophy',
  upload: 'upload',
  user: 'user',
  users: 'users',
  'user-check': 'user-check',
  'user-pen': 'user-pen',
  'user-plus': 'user-plus',
  wrench: 'wrench',
  'angle-down': 'chevron-down',
  'angle-left': 'chevron-left',
  'angle-right': 'chevron-right',
  'angle-up': 'chevron-up',
  'angles-down': 'chevrons-down',
  'angles-left': 'chevrons-left',
  'angles-right': 'chevrons-right',
  'angles-up': 'chevrons-up',
  'arrow-down': 'arrow-down',
  'arrow-left': 'arrow-left',
  'arrow-line-left': 'arrow-left-from-line',
  'arrow-line-right': 'arrow-right-from-line',
  'arrow-right': 'arrow-right',
  'arrow-rotate-left': 'rotate-ccw',
  'arrow-rotate-right': 'rotate-cw',
  'arrow-up': 'arrow-up',
  'arrows-rotate': 'refresh-ccw',
  'caret-down': 'chevron-down',
  'caret-left': 'chevron-left',
  'caret-right': 'chevron-right',
  'caret-up': 'chevron-up',
  'chevron-down': 'chevron-down',
  'chevron-left': 'chevron-left',
  'chevron-right': 'chevron-right',
  'chevron-up': 'chevron-up',
  'address-book': 'book-user',
  'address-card': 'id-card',
  at: 'at-sign',
  backward: 'rewind',
  'backward-fast': 'skip-back',
  'backward-step': 'step-back',
  bars: 'menu',
  'bars-staggered': 'menu',
  'bell-slash': 'bell-off',
  'book-open-reader': 'book-open-text',
  'bookmark-delete': 'bookmark-x',
  'box-archive': 'archive',
  bullhorn: 'megaphone',
  bullseye: 'target',
  'cake-candles': 'cake',
  'calendar-day': 'calendar-days',
  category: 'lock',
  certificate: 'award',
  'check-to-slot': 'vote',
  'circle-exclamation': 'circle-alert',
  'circle-info': 'info',
  'circle-question': 'circle-help',
  'circle-xmark': 'circle-x',
  'clock-rotate-left': 'rotate-ccw',
  'cloud-arrow-down': 'cloud-download',
  'cloud-arrow-up': 'cloud-upload',
  comment: 'message-circle',
  'comment-dots': 'message-circle-more',
  'comment-slash': 'message-circle-off',
  comments: 'messages-square',
  compress: 'minimize',
  crosshairs: 'crosshair',
  cube: 'cuboid',
  cubes: 'boxes',
  desktop: 'monitor',
  'diagram-project': 'workflow',
  'earth-americas': 'globe',
  ellipsis: 'ellipsis',
  'ellipsis-vertical': 'ellipsis-vertical',
  envelope: 'mail',
  'eye-dropper': 'pipette',
  'eye-low-vision': 'eye-off',
  'eye-slash': 'eye-off',
  'face-angry': 'angry',
  'face-meh': 'meh',
  'face-smile': 'smile',
  'file-arrow-up': 'file-up',
  'file-audio': 'file-volume',
  'file-lines': 'file-text',
  'file-signature': 'file-pen',
  filter: 'funnel',
  'filter-circle-xmark': 'funnel-x',
  flask: 'flask-conical',
  'floppy-disk': 'save',
  font: 'type',
  forward: 'play',
  'forward-fast': 'fast-forward',
  'forward-step': 'skip-forward',
  gavel: 'gavel',
  gear: 'settings',
  'grip-lines': 'grip-horizontal',
  handshake: 'handshake',
  'handshake-angle': 'handshake',
  hashtag: 'hash',
  'hourglass-start': 'hourglass',
  language: 'languages',
  'layer-group': 'layers',
  'left-right': 'arrow-left-right',
  like: 'heart',
  'link-slash': 'link-2-off',
  'list-ol': 'list-ordered',
  'list-ul': 'list',
  'location-dot': 'map-pin',
  'location-pin': 'map-pin',
  'magnifying-glass': 'search',
  'magnifying-glass-minus': 'zoom-out',
  'magnifying-glass-plus': 'zoom-in',
  'microphone-slash': 'mic-off',
  'mobile-screen-button': 'smartphone',
  'paper-plane': 'send',
  'pen-to-square': 'square-pen',
  'puzzle-piece': 'puzzle',
  'quote-left': 'quote',
  'quote-right': 'quote',
  repeat: 'repeat',
  'right-from-bracket': 'log-out',
  'right-left': 'arrow-left-right',
  'right-to-bracket': 'log-in',
  robot: 'bot',
  rotate: 'rotate-ccw',
  'share-nodes': 'share-2',
  'shield-halved': 'shield-half',
  sliders: 'sliders-horizontal',
  sort: 'arrow-up-down',
  spinner: 'loader',
  'square-full': 'square',
  'table-cells': 'grid-3x3',
  'table-columns': 'columns-2',
  'temperature-three-quarters': 'thermometer',
  thumbtack: 'pin',
  'thumbtack-slash': 'pin-off',
  'toggle-off': 'toggle-left',
  'toggle-on': 'toggle-right',
  'trash-can': 'trash',
  'triangle-exclamation': 'triangle-alert',
  'truck-medical': 'ambulance',
  unlock: 'lock-open',
  'unlock-keyhole': 'lock-keyhole-open',
  'up-down': 'arrow-up-down',
  'up-right-from-square': 'external-link',
  'user-gear': 'user-cog',
  'user-group': 'users',
  'user-secret': 'hat-glasses',
  'user-shield': 'shield-user',
  'user-xmark': 'user-x',
  'volume-high': 'volume-2',
  'volume-xmark': 'volume-x',
  'wand-magic': 'wand-sparkles',
  'wand-magic-sparkles': 'wand-sparkles',
  xmark: 'x',
  'far-bell': 'bell',
  'far-bell-slash': 'bell-off',
  'far-bookmark': 'bookmark',
  'far-calendar-plus': 'calendar-plus',
  'far-chart-bar': 'chart-bar',
  'far-circle': 'circle',
  'far-circle-dot': 'circle-dot',
  'far-circle-question': 'circle-question-mark',
  'far-clipboard': 'clipboard',
  'far-clock': 'clock',
  'far-comment': 'message-square',
  'far-comments': 'messages-square',
  'far-copy': 'copy',
  'far-copyright': 'copyright',
  'far-credit-card': 'credit-card',
  'far-envelope': 'mail',
  'far-eye': 'eye',
  'far-eye-slash': 'eye-off',
  'far-face-frown': 'frown',
  'far-face-meh': 'meh',
  'far-face-smile': 'smile',
  'far-file-lines': 'file-text',
  'far-heart': 'heart',
  'far-image': 'image',
  'far-moon': 'moon',
  'far-pen-to-square': 'square-pen',
  'far-square': 'square',
  'far-square-check': 'square-check',
  'far-star': 'star',
  'far-sun': 'sun',
  'far-thumbs-down': 'thumbs-down',
  'far-thumbs-up': 'thumbs-up',
  'far-trash-can': 'trash'
};

/**
 * Os que ficam fora do mapa de propósito (`square-full` e `circle`). O
 * `circle` sólido fica no
 * Font Awesome porque é usado como bolinha cheia (status, não lido). O
 * coração cheio de quem curtiu não tem nome no Flarum: o tema o reproduz preenchendo o coração do Lucide pelo CSS (Icons.less).
 */
export const META_IGNORED = new Set(['circle', 'square-full']);

/**
 * Ícones de interface que o Flarum e as extensões usam e que a tabela
 * principal não cobre. Sem eles, a casca misturaria traço
 * e sólido na mesma barra; o equivalente escolhido é o do próprio Lucide.
 */
export const CANELA_EXTRAS = {
  // Editor (barra de formatação do composer e do markdown/bbcode)
  heading: 'heading',
  strikethrough: 'strikethrough',
  eraser: 'eraser',
  terminal: 'square-terminal',
  // Ações de post e de tópico
  'reply-all': 'reply-all',
  trash: 'trash',
  'lock-open': 'lock-open',
  'arrow-up-from-bracket': 'share',
  'arrows-left-right': 'arrow-left-right',
  'rotate-left': 'rotate-ccw',
  'code-branch': 'git-branch',
  print: 'printer',
  // Tela cheia e minimizar do composer → maximize/minimize
  expand: 'maximize',
  'up-right-and-down-left-from-center': 'maximize',
  'down-left-and-up-right-to-center': 'minimize',
  // Usuários e moderação
  'user-minus': 'user-minus',
  'user-slash': 'user-x',
  shield: 'shield',
  'grip-vertical': 'grip-vertical',
  // Mensagens, arquivos e enquetes (flarum/messages, fof/upload, fof/polls)
  message: 'message-square',
  'envelope-open': 'mail-open',
  'book-open': 'book-open',
  'file-code': 'file-code',
  'file-zipper': 'file-archive',
  'file-csv': 'file-spreadsheet',
  'file-excel': 'file-spreadsheet',
  'file-pdf': 'file-text',
  'file-word': 'file-text',
  'file-powerpoint': 'file-text',
  'file-contract': 'file-text',
  'file-export': 'file-output',
  'photo-film': 'images',
  'square-poll-horizontal': 'chart-bar',
  'square-poll-vertical': 'chart-column',
  'table-cells-large': 'grid-2x2',
  'pen-ruler': 'pencil-ruler',
  feather: 'feather',
  // Bilhete (botões de extensões na barra do composer): no sólido do FA ele
  // destoava, menor e preenchido, entre os ícones de traço.
  'sticky-note': 'sticky-note',
  'note-sticky': 'sticky-note',
  // O sino de "acompanhando": com ondas,
  // diferente do sino simples de "normal".
  'bell-ring': 'bell-ring',
  // E o de "monitorando", com o ponto.
  'bell-dot': 'bell-dot',
  'calendar-days': 'calendar-days',
  'circle-dot': 'circle-dot',
  // O seletor de tema do core usa o laptop para "automático"
  laptop: 'laptop',
  // Ícones que o admin costuma dar às etiquetas do Flarum: sem o traço, a
  // seção "Etiquetas" da barra misturaria sólido e traço.
  server: 'server',
  'universal-access': 'accessibility',
  // O seletor "Recentes ⌃⌄" da lista no celular, desenhado como
  // chevrons-up-down.
  'chevron-expand': 'chevrons-up-down'
};

/** Classes do Font Awesome que são estilo, não nome de ícone. */
const STYLE_CLASSES = new Set(['fa', 'fas', 'far', 'fab', 'fal', 'fat', 'fad', 'fa-solid', 'fa-regular', 'fa-brands', 'fa-light', 'fa-thin', 'fa-duotone', 'fa-sharp', 'fa-sharp-duotone']);

/** Utilitários do Font Awesome (`fa-fw`, `fa-spin`, `fa-2x`…), que também começam com `fa-`. */
const UTILITY = /^fa-(fw|spin|spin-pulse|spin-reverse|pulse|beat|beat-fade|fade|bounce|shake|flip|border|inverse|li|ul|stack|stack-1x|stack-2x|lg|sm|xs|2xs|xl|2xl|\dx|10x|rotate-\d+|rotate-by|flip-\w+|pull-\w+|swap-opacity|width-auto)$/;

/**
 * O nome do Lucide para uma classe de ícone do Flarum (`fas fa-search`,
 * `far fa-bell fa-fw`…), ou `null` quando o ícone deve continuar no Font
 * Awesome: marcas (`fab`), os ignorados (META_IGNORED) e qualquer um fora do mapa,
 * como boa parte dos que o admin escolhe para tags e grupos.
 */
export function lucideNameFor(classes) {
  let regular = false;
  let brand = false;
  let icon = null;
  for (const cls of classes.split(/\s+/)) {
    if (!cls) continue;
    if (cls === 'far' || cls === 'fa-regular') regular = true;else if (cls === 'fab' || cls === 'fa-brands') brand = true;else if (icon === null && !STYLE_CLASSES.has(cls) && cls.startsWith('fa-') && !UTILITY.test(cls)) icon = cls.slice(3);
  }
  if (!icon || brand) return null;
  const name = FA_ALIASES[icon] ?? icon;

  // No mapa o regular tem chave própria (`far-bell`). No Flarum o
  // regular e o sólido do mesmo nome são o mesmo ícone de interface, e no
  // Lucide (só traço) os dois sairiam iguais: daí o recuo para o sólido.
  const regularName = regular ? META_MAP[`far-${name}`] : undefined;
  if (regularName) return regularName;
  if (!regular && META_IGNORED.has(name)) return null;
  return META_MAP[name] ?? CANELA_EXTRAS[name] ?? null;
}
flarum.reg.add('ramon-canela', 'forum/icons/iconMap', { FA_ALIASES: FA_ALIASES,META_MAP: META_MAP,META_IGNORED: META_IGNORED,CANELA_EXTRAS: CANELA_EXTRAS,lucideNameFor: lucideNameFor, });